import React from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiBookOpen, FiAward, FiGlobe, FiTarget, FiHeart } from 'react-icons/fi';
import ScrollAnimation from '../components/ui/ScrollAnimation';

const About = () => {
  const stats = [
    { icon: FiUsers, number: '10,000+', label: '학습자' },
    { icon: FiBookOpen, number: '500+', label: '강의' },
    { icon: FiAward, number: '50+', label: '전문 강사' },
    { icon: FiGlobe, number: '30+', label: '국가' }
  ];

  const values = [
    {
      icon: FiTarget,
      title: '목표 지향적 학습',
      description: '각 학습자의 목표에 맞는 맞춤형 커리큘럼을 제공하여 효율적인 학습을 지원합니다.'
    },
    {
      icon: FiHeart,
      title: '학습자 중심',
      description: '학습자의 성공을 최우선으로 생각하며, 지속적인 성장을 돕습니다.'
    },
    {
      icon: FiAward,
      title: '품질 보장',
      description: '엄선된 전문 강사진과 검증된 콘텐츠로 최고의 교육 품질을 제공합니다.'
    },
    {
      icon: FiGlobe,
      title: '글로벌 접근성',
      description: '언제 어디서나 접근 가능한 온라인 플랫폼으로 학습의 장벽을 낮춥니다.'
    }
  ];

  const team = [
    {
      name: '김민수',
      position: 'CEO & Founder',
      bio: '10년 이상의 교육 기술 분야 경험을 바탕으로 EnglishPT를 설립했습니다.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face'
    },
    {
      name: '이지영',
      position: 'Head of Education',
      bio: '교육 콘텐츠 개발과 강사 관리에 특화된 교육 전문가입니다.',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop&crop=face'
    },
    {
      name: '박현우',
      position: 'CTO',
      bio: '최신 기술을 활용하여 사용자 친화적인 학습 플랫폼을 구축합니다.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-subtle py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollAnimation animation="fadeInUp" delay={0.2}>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              EnglishPT에 오신 것을 환영합니다
            </h1>
          </ScrollAnimation>
          <ScrollAnimation animation="fadeInUp" delay={0.4}>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              EnglishPT는 혁신적인 온라인 영어 교육 플랫폼입니다. 
              전 세계 학습자들이 언제 어디서나 최고의 영어 교육을 받을 수 있도록 
              최신 기술과 전문 강사진을 통해 차별화된 학습 경험을 제공합니다.
            </p>
          </ScrollAnimation>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <ScrollAnimation key={index} animation="fadeInUp" delay={0.1 * index}>
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <stat.icon className="h-12 w-12 text-primary-800" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">우리의 미션</h2>
              <p className="text-lg text-gray-600 mb-6">
                EnglishPT는 언어의 장벽을 허물고, 모든 사람이 글로벌 커뮤니케이션의 
                기회를 가질 수 있도록 돕는 것을 목표로 합니다.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                최신 교육 기술과 전문 강사진을 통해 개인화된 학습 경험을 제공하며, 
                학습자들이 자신의 목표를 달성할 수 있도록 지속적으로 지원합니다.
              </p>
              <Link
                to="/courses"
                className="inline-block bg-primary-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
              >
                강의 둘러보기
              </Link>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-soft">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">핵심 가치</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-800 rounded-full mt-2"></div>
                  <span className="text-gray-700">개인화된 학습 경험</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-800 rounded-full mt-2"></div>
                  <span className="text-gray-700">전문 강사진의 품질 보장</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-800 rounded-full mt-2"></div>
                  <span className="text-gray-700">혁신적인 교육 기술</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-800 rounded-full mt-2"></div>
                  <span className="text-gray-700">지속적인 학습 지원</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">우리의 가치</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              EnglishPT가 추구하는 핵심 가치들을 통해 최고의 교육 경험을 제공합니다.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <value.icon className="h-12 w-12 text-primary-800" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">우리 팀</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              EnglishPT의 성공을 이끌어가는 전문가들을 소개합니다.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow-soft p-6 text-center">
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-primary-100 flex items-center justify-center" style={{ display: 'none' }}>
                    <span className="text-primary-800 font-bold text-2xl">
                      {member.name.charAt(0)}
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-primary-800 font-medium mb-3">{member.position}</p>
                <p className="text-gray-600">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">지금 시작하세요</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            EnglishPT와 함께 영어 학습의 새로운 경험을 시작하세요. 
            당신의 목표 달성을 위해 최선을 다하겠습니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-block bg-white text-primary-800 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200"
            >
              무료 회원가입
            </Link>
            <Link
              to="/contact"
              className="inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-primary-800 transition-colors duration-200"
            >
              문의하기
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About; 